import React from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';

import { BackgroundView } from '../background';
import { PageContext } from '../page';
import { WidgetsContext } from '../core/context';
import { useWidgetChildrenSelector } from '../core/hooks';
import { getBoxShadow } from '../../../services/cssHelper.service';
import { allColumnsInFlexMode, CONTENT_DRIVEN_SECTION_HEIGHT, EMPTY_CONTENT_SECTION_MIN_HEIGHT, flexValueToCss } from '../flex/flexContext';

import { INTERACTIVE_CONTENT_TYPES, PAGE_CONTAIN_STORYBOARDS } from './../../../constants/storyboardTypes';
import { resetMarginOnTablet, useInteractiveContent } from '../InteractiveContent/v2';
import styles from './section.module.scss';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import size from 'lodash/size';


const SectionWidgetComponent = ({ className, style, widgetProps, breakpoint, children, inFlexFlow, parentFlexLayout, isFirstFlowedSibling, ...rest }) => {

  const context = React.useContext(WidgetsContext);
  const { pageWidth: pageWidthFromContext = 1200 } = React.useContext(PageContext);
  const pageWidth = parseInt(pageWidthFromContext, 10) || 1200;
  const storyboardType = get(context, 'storyboard.type');
  const allWidgets = useSelector((state) => get(state, `${context.selector}.widgets`) || []);

  const id = get(widgetProps, 'uid');
  const type = get(widgetProps, 'type');

  const stretched = get(widgetProps, 'properties.desktop.stretched', false) && !INTERACTIVE_CONTENT_TYPES.includes(storyboardType);
  const background = get(widgetProps, 'properties.desktop.background');

  const columns = size(children);
  const columnSpacing = get(widgetProps, `properties.${breakpoint}.columnSpacing`, 0);
  const adjustedColumnSpacing = columnSpacing * ((columns - 1) / columns);
  const { tabletScreens = false } = useInteractiveContent()
  const columnWidgets = useWidgetChildrenSelector(id) || [];
  const allColumnsFlex = allColumnsInFlexMode(columnWidgets);
  const sectionHasContent = columnWidgets.some((column) =>
    columnHasWidgetDescendant(column.uid, allWidgets)
  );

  var widgetStyle = {
    ...style,
    ...getStyles(widgetProps, breakpoint, storyboardType, allColumnsFlex, sectionHasContent, inFlexFlow, parentFlexLayout, isFirstFlowedSibling, pageWidth),
    ...resetMarginOnTablet(tabletScreens)
  }

  return (
    <div id={id}
      className={cx(className, styles.section, 'cs_section', {
        [styles.stretched]: stretched,
        [styles.inFlexFlow]: inFlexFlow,
        [styles.mobile]: breakpoint === 'mobile'
      })}
      style={widgetStyle}
      data-widget-id={id}
      data-widget-type={type}
    >
      <BackgroundView background={background} breakpoint={breakpoint} />
      {React.Children.map(children,
        child => {
          return (
            React.cloneElement(child, {
              columnGap: adjustedColumnSpacing,
              sectionStretched: stretched
            }, null)
          );
        })
      }
    </div>
  );
};

function columnHasWidgetDescendant(columnUid, allWidgets) {
  return allWidgets.some((widget) => {
    let parentId = widget.parent;

    while (parentId) {
      if (parentId === columnUid) {
        return true;
      }

      const parentWidget = allWidgets.find((item) => item.uid === parentId);
      parentId = parentWidget?.parent;
    }

    return false;
  });
}

function getStyles(widgetProps, breakpoint, storyboardType, allColumnsFlex, sectionHasContent, inFlexFlow, parentFlexLayout, isFirstFlowedSibling, pageWidth = 1200) {
  const stretched = get(widgetProps, 'properties.desktop.stretched', false);
  const margin = get(widgetProps, 'properties.desktop.margin', 0);
  const padding = get(widgetProps, `properties.${breakpoint}.padding`, 0);
  const columnSpacing = get(widgetProps, `properties.${breakpoint}.columnSpacing`, 0);

  var widgetStyles = {
    paddingTop: `${padding}px`,
    paddingBottom: `${padding}px`
  }

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      width: `1200px`,
      columnGap: `${columnSpacing}px`,
      paddingLeft: `${padding}px`,
      paddingRight: `${padding}px`
    }

    let translateY = get(widgetProps, 'properties.desktop.layout.transform.translate', '')?.split(",")?.[1] || '0px';

    if (stretched && !PAGE_CONTAIN_STORYBOARDS.includes(storyboardType)) {
        const viewportWidth = `calc(100vw - var(--scrollbar-width, 0px))`;
        const stretchedWidth = `calc(${viewportWidth} - 2 * ${margin}px)`;

        if (inFlexFlow) {
          const isNoWrapRow =
            flexValueToCss(get(parentFlexLayout, 'flexDirection')) === 'row' &&
            flexValueToCss(get(parentFlexLayout, 'flexWrap')) === 'nowrap';

          delete widgetStyles.width;

          if (isNoWrapRow) {
            widgetStyles = {
              ...widgetStyles,
              '--cs-section-flow-width': stretchedWidth,
              marginLeft: isFirstFlowedSibling
                ? `calc((${viewportWidth} - ${pageWidth}px) / -2)`
                : 0,
              marginRight: 0,
            }
          } else {
            widgetStyles = {
              ...widgetStyles,
              '--cs-section-flow-width': stretchedWidth,
              width: stretchedWidth,
              marginLeft: `calc((100% - (${stretchedWidth})) / 2)`,
              marginRight: `calc((100% - (${stretchedWidth})) / 2)`,
            }
          }

        } else {
          widgetStyles = {
            ...widgetStyles,
            marginLeft: margin,
            width: `calc(100% - (2 * ${margin}px))`,
            transform: `translate(0px, ${translateY})`,
          }
        }
    }

    if (stretched && PAGE_CONTAIN_STORYBOARDS.includes(storyboardType)) {
        widgetStyles = {
          ...widgetStyles,
          marginLeft: margin,
          width: (inFlexFlow ? "100vw" :`calc(100% - (2 * ${margin}px))`),
          ...(!inFlexFlow && { transform: `translate(0px, ${translateY})` }),
        }

        if(PAGE_CONTAIN_STORYBOARDS.includes(storyboardType)) {
          widgetStyles = {
            ...widgetStyles,
            width: `1200px`,
            marginLeft: `calc(50% - (1200px / 2))`,
          }
        }

        if (inFlexFlow && INTERACTIVE_CONTENT_TYPES.includes(storyboardType)) {
          widgetStyles = {
            ...widgetStyles,
            marginLeft: 0,
            marginRight: 0,
          }
        }
    }

    if (inFlexFlow && !stretched) {
      widgetStyles = {
        ...widgetStyles,
        maxWidth: '100%',
      }
    }
  }

  if (breakpoint === 'mobile') {
    let translateY = get(widgetProps, 'properties.mobile.layout.transform.translate', '')?.split(",")?.[1] || '0px';
    widgetStyles = {
      ...widgetStyles,
      width: `100%`,
      rowGap: `${columnSpacing}px`,
      marginLeft: 0,
      marginRight: 0,
      '--cs-section-flow-width': '100%',
      ...(!inFlexFlow && { transform: `translate(0px, ${translateY})` }),
    }
  }


  if (allColumnsFlex) {
    widgetStyles.height = CONTENT_DRIVEN_SECTION_HEIGHT;
    widgetStyles.minHeight = sectionHasContent ? 0 : EMPTY_CONTENT_SECTION_MIN_HEIGHT;
  }

  //DropShadow
  const shadow = get(widgetProps, `properties.desktop.shadow`, {});
  const shadowStyle = getBoxShadow(shadow);
  widgetStyles = {
    ...widgetStyles,
    ...shadowStyle
  }

  return widgetStyles;
}

const sectionWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint', 'children', 'inFlexFlow', 'parentFlexLayout', 'isFirstFlowedSibling'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const SectionWidget = React.memo(SectionWidgetComponent, sectionWidgetEquals);
